import * as React from 'react'

import { LuSearch, LuX } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Spinner from '@/components/common/Spinner'
import ErrorMessage from '@/components/form/ErrorMessage'

export const TEXT_INPUT_CLASSNAME = twMerge(
  'text-primary-900 dark:text-primary-100 file:text-primary-900 dark:file:text-primary-100 placeholder:text-primary-500 dark:placeholder:text-primary-400 selection:bg-primary-300 selection:text-primary-700 dark:selection:bg-primary-600 dark:selection:text-primary-100',
  'border-primary-200 dark:border-primary-800 flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-sm bg-primary-50 dark:bg-primary-900 transition-[color,box-shadow] outline-none',
  'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
  'focus-visible:border-accent-400 focus-visible:ring-accent-400 dark:focus-visible:border-accent-400/50 dark:focus-visible:ring-accent-400/50 focus-visible:ring-[2px]',
  // Red error treatment, matching the Radio group's invalid state.
  'aria-invalid:border-red-400 aria-invalid:ring-2 aria-invalid:ring-red-400/20 dark:aria-invalid:border-red-400 dark:aria-invalid:ring-red-400/30',
)

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        data-slot="input"
        className={twMerge(TEXT_INPUT_CLASSNAME, className)}
        ref={ref}
        {...props}
      />
    )
  },
)

const FormInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & {
    error?: string
    containerClassName?: string
  }
>(({ className, type, error, containerClassName, ...props }, ref) => {
  return (
    <div className={twMerge('flex flex-col', containerClassName)}>
      <Input {...props} className={className} ref={ref} />
      {error && <ErrorMessage className="mt-[2px]">{error}</ErrorMessage>}
    </div>
  )
})

Input.displayName = 'Input'

FormInput.displayName = 'FormInput'

interface SearchInputProps extends React.ComponentProps<'input'> {
  containerClassName?: string
  iconSize?: number
  onClear?: () => void
  isLoading?: boolean
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      containerClassName,
      iconSize = 14,
      onClear,
      isLoading = false,
      placeholder = 'Search...',
      ...props
    },
    ref,
  ) => {
    const searchValue = props.value
    const showClearButton =
      !!onClear && typeof searchValue === 'string' && searchValue.length > 0

    return (
      <div className={twMerge('relative', containerClassName)}>
        {isLoading ? (
          <div className="absolute left-2 top-1/2 -translate-y-1/2">
            <Spinner className="w-3.5 h-3.5" />
          </div>
        ) : (
          <LuSearch
            size={iconSize}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-primary-500 dark:text-primary-400"
          />
        )}
        <Input
          type="text"
          placeholder={placeholder}
          className={twMerge(
            'h-7 pl-[30px] text-sm border-primary-300 dark:border-primary-700 rounded-[5px]',
            showClearButton ? 'pr-8' : 'pr-3',
            className,
          )}
          ref={ref}
          {...props}
        />
        {showClearButton ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-primary-500 transition-colors hover:text-primary-800 active:scale-100 dark:text-primary-400 dark:hover:text-primary-100"
          >
            <LuX size={14} />
          </button>
        ) : null}
      </div>
    )
  },
)

SearchInput.displayName = 'SearchInput'

export { Input, FormInput, SearchInput }
